'use client';

import { createContext, useContext, useMemo, PropsWithChildren } from 'react';
import { useStatsQuery } from './queries.generated';
import { RestaurantsFilterContext, useDataFilters } from '@/components/filters/use-data-filters';
import { useDebounce } from '@instamenu/react-hooks';

export const SalesSummaryContext = createContext<
  | (Pick<
      RestaurantsFilterContext,
      'restaurants' | 'timespan' | 'times' | 'setTimespan' | 'setTimes'
    > & {
      statsQuery: ReturnType<typeof useStatsQuery>;
      statsQueryVariables: {
        restaurantIds: string[];
        startDate: string;
        endDate: string;
        startTime: number | undefined;
        endTime: number | undefined;
      };
    })
  | undefined
>(undefined);

export const SalesSummaryProvider = ({ children }: PropsWithChildren) => {
  const { restaurants, times, timespan, setTimes, setTimespan } = useDataFilters();
  const statsQueryVariables = useMemo(() => {
    return {
      restaurantIds: restaurants.map(({ id }) => id),
      startDate: timespan.start.toISODate(),
      endDate: timespan.end.toISODate(),
      startTime: times.startTime,
      endTime: times.endTime,
    };
  }, [restaurants, times.endTime, times.startTime, timespan]);
  const debounceOptions = useMemo(() => ({ leading: true }), []);
  const queryVars = useDebounce(statsQueryVariables, 1000, debounceOptions);
  const executeQuery = queryVars.restaurantIds && queryVars.restaurantIds.length > 0;
  const statsQuery = useStatsQuery({
    variables: queryVars,
    pause: !executeQuery,
  });

  const value = {
    statsQuery,
    statsQueryVariables,
    restaurants,
    timespan,
    setTimespan,
    times,
    setTimes,
  };
  return <SalesSummaryContext.Provider value={value}>{children}</SalesSummaryContext.Provider>;
};

export function useSalesSummaryContext() {
  const ctx = useContext(SalesSummaryContext);
  if (!ctx) throw new Error('useSalesSummaryContext must be used within SalesSummaryProvider');
  return ctx;
}
